// ============================================================
// STACKING (v8.367.0) - what happens when a condition is applied to
// somebody who already has it.
//
// The old rule was one session boolean that also decided whether clocks
// tick at all, and the merge keyed on the whole code - so Movement +10
// twice became +10 for six rounds while +10 and +20 stood side by side
// and summed. A policy names the answer; the merge keys on the SCOPE of
// a custom modifier, not its value, so all four are possible.
//
// Pure, and zero React dependency.
// ============================================================

export type StackPolicy = 'refresh' | 'extend' | 'raise' | 'stack';

/** Today's behaviour under the countdown: clocks add. */
export const DEFAULT_STACK_POLICY: StackPolicy = 'extend';

export interface StackPolicyEntry {
  key: StackPolicy;
  label: string;
  hint: string;
}

/** The rulings on offer, in the order the GM meets them. */
export const STACK_POLICIES: StackPolicyEntry[] = [
  {
    key: 'refresh',
    label: 'Refresh - the stronger value, the longer clock',
    hint: 'Applying it again never accumulates: +10 for 3 then +20 for 2 is one +20 for 3. The rulebook reading for most conditions.',
  },
  {
    key: 'extend',
    label: 'Extend - the stronger value, clocks add',
    hint: '+10 for 3 then +20 for 2 is one +20 for 5. Stunned 3 then Stunned 2 is Stunned 5 - what "additive" meant before v8.367.0.',
  },
  {
    key: 'raise',
    label: 'Raise - values add, the longer clock',
    hint: '+10 for 3 then +20 for 2 is one +30 for 3, clamped to a cap when one is set. Poison that builds, fatigue that mounts.',
  },
  {
    key: 'stack',
    label: 'Stack - keep both, side by side',
    hint: 'Two chips, each with its own clock; their values still add wherever they are read. Only exact duplicates collapse.',
  },
];

export function isStackPolicy(value: unknown): value is StackPolicy {
  return value === 'refresh' || value === 'extend' || value === 'raise' || value === 'stack';
}

/** The old boolean, for callers not yet converted: additive → extend, else refresh. */
export function stackPolicyOf(value: StackPolicy | boolean | undefined | null): StackPolicy {
  if (isStackPolicy(value)) return value;
  return value === true ? 'extend' : 'refresh';
}
